import { components, ComponentCategory } from '@smartphone-tycoon/shared-core';
import type { Product } from '@/types/tycoon';
import { GAME_CONSTANTS } from '../config/gameConstants';
import { PRODUCTION_FORMULAS } from '../config/gameFormulas';
import { roundToThousandths } from '@smartphone-tycoon/shared-core';

export class ProductMetricsService {
  private static getComponentCost(categoryId: ComponentCategory['id'], value: string): number {
    const category = components.find(c => c.id === categoryId);
    const option = category?.options.find(o => o.value === value);
    return option?.cost || 0;
  }

  private static getComponentTechScore(categoryId: ComponentCategory['id'], value: string): number {
    const category = components.find(c => c.id === categoryId);
    const option = category?.options.find(o => o.value === value);
    return (option?.techScore || 0) * (category?.weight || 1);
  }

  private static getTotalComponentWeights(): number {
    return components.reduce((sum, category) => sum + category.weight, 0);
  }

  static calculateTotalCost(selectedComponents: Record<ComponentCategory['id'], string>): number {
    return Object.entries(selectedComponents).reduce((total, [categoryId, value]) => {
      return total + this.getComponentCost(categoryId as ComponentCategory['id'], value);
    }, 0);
  }

  static calculateTechScore(selectedComponents: Record<ComponentCategory['id'], string>): number {
    const totalScore = Object.entries(selectedComponents).reduce((total, [categoryId, value]) => {
      return total + this.getComponentTechScore(categoryId as ComponentCategory['id'], value);
    }, 0);

    const totalWeight = this.getTotalComponentWeights();
    return roundToThousandths(totalScore / totalWeight);
  }

  static calculateValueScore(techScore: number, price: number): number {
    const basePrice = GAME_CONSTANTS.productMetrics.basePriceReference;
    const priceRatio = roundToThousandths(basePrice / price);
    const weightedScore = roundToThousandths(
      (techScore * GAME_CONSTANTS.productMetrics.techScoreWeight) + 
      ((priceRatio * 100) * GAME_CONSTANTS.productMetrics.priceRatioWeight)
    );
    return Math.round(Math.min(100, Math.max(0, weightedScore)));
  }

  static calculateUserSatisfaction(techScore: number, valueScore: number): number {
    return Math.round(
      (techScore * GAME_CONSTANTS.productMetrics.userSatisfactionTechWeight + 
       valueScore * GAME_CONSTANTS.productMetrics.userSatisfactionValueWeight) * 
      GAME_CONSTANTS.productMetrics.satisfactionMultiplier
    );
  }

  static calculateQualityScore(product: Product, qualityBonus: number = 0): number {
    const baseQuality = product.designConfig.techScore * GAME_CONSTANTS.productMetrics.qualityBaseMultiplier;
    const complexityRange = GAME_CONSTANTS.productMetrics.complexityFactorMax - GAME_CONSTANTS.productMetrics.complexityFactorMin;
    const complexityFactor = GAME_CONSTANTS.productMetrics.complexityFactorMin + (Math.random() * complexityRange);
    const quality = baseQuality * complexityFactor * (1 + qualityBonus);
    return Math.min(100, Math.round(quality));
  }

  static calculateDefectRate(product: Product): number {
    const baseDefectRate = PRODUCTION_FORMULAS.getDefectRate(product.designConfig.techScore);
    return roundToThousandths(PRODUCTION_FORMULAS.getDefectRateWithVariation(baseDefectRate));
  }

  static calculateProductionCost(components: Record<string, string>): number {
    // Calculate base cost from components
    const componentsCost = this.calculateTotalCost(components);
    return PRODUCTION_FORMULAS.getProductionCost(componentsCost);
  }
} 